import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  ViewChild,
  OnInit,
} from '@angular/core';
import { Communication, LikeableContent, Message, ReportDialogInput } from '../../interface';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { SkyI18nModule, registerTranslationsManuallyStatic } from '@skyeng/libs/shared/i18n';
import { MessageType } from '../../const';
import { Store } from '@skyeng/libs/shared/store';
import { CommunicationFormService } from '../communication-form/communication-form.service';
import { debounceTime, distinctUntilChanged, filter, map, startWith, switchMap, tap } from 'rxjs/operators';
import { DecorateUntilDestroy, filterEmpty, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';
import { VimCoreCurrentUserService, VimCoreUserModule } from '@skyeng/libs/vimbox-core/user';
import { BehaviorSubject, combineLatest, fromEvent } from 'rxjs';
import { CommunicationLikeService } from '../../service/communication-like.service';
import { DsDialogService } from '@skyeng/libs/shared/ui-kit-v2/dialog';
import { CommunicationReportDialogComponent } from '../communication-report-dialog/communication-report-dialog';
import { AsyncPipe } from '@angular/common';
import { CommunicationLikeDirective } from '../communication-like/communication-like.directive';
import { CommunicationsMessageComponent } from '../communication-message/communication-message';
import { CommunicationLikeComponent } from '../communication-like/communication-like';
import { CommunicationsThreadComponent } from '../communication-thread/communication-thread';
import { CommunicationsFormComponent } from '../communication-form/communication-form';
import { DsButtonModule } from '@skyeng/libs/shared/ui-kit-v2/button';
import { TypografModule } from '@skyeng/libs/skypro/misc';
import empty1 from './images/empty.svg';
import entranslation from '../../translation/en.json';
import rutranslation from '../../translation/ru.json';

@DecorateUntilDestroy()
@Component({
  selector: 'skypro-communication',
  templateUrl: 'communication.html',
  styleUrls: ['communication.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SkyI18nModule,
    VimCoreUserModule,
    CommunicationsFormComponent,
    CommunicationsThreadComponent,
    CommunicationLikeComponent,
    CommunicationsMessageComponent,
    CommunicationLikeDirective,
    DsButtonModule,
    AsyncPipe,
    TranslocoModule,
    TypografModule,
  ],
})
export class CommunicationComponent implements OnInit, OnChanges {
  @Input() communicationId: number | null;
  @Input() communicationModifierCallback?: (communication: Communication) => Communication;
  @Input() showEmptyDeletedThreads = false;
  // В режиме readOnly нельзя создавать новые треды, а также отвечать в существующих
  @Input() readOnly = false;

  // тред выделен на странице, если указан, то включится режим "показывать только подсвеченный тред", его можно отключить кнопкой "Показать все"
  @Input() highlightedThreadId: number | null = null;

  // Показывать ли форму "задать вопрос"
  @Input() showCreateThreadForm = true;

  // Текущий юзер может удалять все сообщения: он админ или куратор
  @Input() canDeleteAnyMessage = false;
  @Input() canClose = false;
  @Input() canPin = false;
  @Input() canHide = false;
  // если false - вообще не показываем меню действий с сообщениями
  @Input() hasActions = true;
  // если false - не показывается сообщение о подгрузке сообщений
  @Input() hasLoader = true;

  // Разрешает выбор собеседника - Н или AI (Герман)
  @Input() canAskTutorOrAI = false;

  @Input() allowedAttachmentTypes: string[] | null = null;

  @Input() hasCompactEmptyView = false;
  @Input() sortByOwnThreads = true;
  @Output() closeThread = new EventEmitter<number>();
  @Output() selectedThreadIdChange = new EventEmitter<number | null>();
  @Output() questionCountChange = new EventEmitter<number>();

  @ViewChild('threadScroll', { read: ElementRef }) threadScroll: ElementRef<HTMLInputElement>;
  @ViewChild('threadsContainer') threadsContainer: ElementRef<HTMLElement>;

  private translocoService = inject(TranslocoService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private communicationFormService = inject(CommunicationFormService);
  private communicationLikeService = inject(CommunicationLikeService);
  private vimCoreCurrentUserService = inject(VimCoreCurrentUserService);
  private dsDialogService = inject(DsDialogService);

  empty = empty1;
  private store = new Store({
    communication: null as Communication | null,
    communicationId: null as number | null,
    selectedThreadId: null as number | null,
    highlightedThreadId: null as number | null,
    editingMessageId: null as number | null,
    replyingThreadId: null as number | null,
    expandedThreadIds: [] as number[],
    isCreateNewThreadFormVisible: false,
    // Флаг загрузки коммуникации внутри шорт поллинга
    isLoading: false,
    showOnlyHighlighted: false,
  });

  // uglyhack: Я не знаю почему, но если сделать тоже самое через store, флаг не выставляется в true при смене коммуникации.
  private isInitalCommunicationLoadingSubj = new BehaviorSubject<boolean>(true);
  // Флаг первичной загрузки коммуникации, выставляется в true при смене коммуникации, не меняется при шорт поллинге
  isInitalCommunicationLoading$ = this.isInitalCommunicationLoadingSubj.asObservable();

  // Id треда до которого до скролил юзер
  private lastThreadSeenByUser$ = combineLatest([
    this.store.select('communicationId'),
    this.store.select('highlightedThreadId'),
  ]).pipe(
    // Когда меняется коммуникация, нам нужно снова начать с null, поэтому используем вложенный поток
    switchMap(() =>
      fromEvent(window, 'scroll', { capture: true }).pipe(
        debounceTime(50),
        filter(() => {
          if (!this.threadsContainer) {
            return false;
          }

          // В режиме отображения одного треда не обновляем курсор
          if (this.store.get('showOnlyHighlighted')) {
            return false;
          }

          const containerHeight = this.threadsContainer.nativeElement.scrollHeight;
          const containerTopPosition = this.threadsContainer.nativeElement.getBoundingClientRect().top;

          const distanceToContainerBottom = containerHeight + containerTopPosition;

          // Значение подобрано экспериментально, чтобы при скроле не было остановок на подгрузку на хорошем интернете
          const DISTANCE_TO_BOOTOM_WHEN_SHOULD_FETCH = 6000;

          const isAllThreadsAlreadyLoaded =
            this.store.get('communication')?.threadsCount === this.store.get('communication')?.threads.length;

          if (isAllThreadsAlreadyLoaded) {
            return false;
          }

          // Когда до нижней границы остается немного - запрашиваем новый чанк
          const shouldRequestNewChank = distanceToContainerBottom < DISTANCE_TO_BOOTOM_WHEN_SHOULD_FETCH;
          return shouldRequestNewChank;
        }),
        map(() => {
          const communication = this.store.get('communication');
          if (!communication) {
            return null;
          }

          const lastThreadId = this.getLastThreadIdFromCommunication(communication);
          return lastThreadId;
        }),
        startWith(null),
      ),
    ),
  );

  // Курсор для запроса данных из АПИ
  lastThreadIdCursor$ = combineLatest([this.lastThreadSeenByUser$, this.store.select('highlightedThreadId')]).pipe(
    map(([lastThreadSeenByUser, highlightedThreadId]) => {
      // Если указан курсор от скрола используем его, если нет, то highlightedThreadId
      return lastThreadSeenByUser ?? highlightedThreadId;
    }),

    distinctUntilChanged(),
  );

  isCreateNewThreadFormVisible$ = this.store.select('isCreateNewThreadFormVisible');
  editingMessageId$ = this.store.select('editingMessageId');

  showOnlyHighlighted$ = this.store.select('showOnlyHighlighted');
  highlightedThreadId$ = this.store.select('highlightedThreadId');

  communication$ = this.store.select('communication').pipe(filterEmpty());

  private hightlightedThreads$ = combineLatest([this.communication$, this.store.select('highlightedThreadId')]).pipe(
    map(([communication, highlightedThreadId]) => {
      if (!highlightedThreadId) {
        return [];
      }

      return communication.threads.filter(thread => thread.id === highlightedThreadId);
    }),
  );

  threadCount$ = this.communication$.pipe(map(({ threadsCount }) => threadsCount));

  threads$ = this.showOnlyHighlighted$.pipe(
    switchMap(showOnlyHighlighted => {
      if (showOnlyHighlighted) {
        return this.hightlightedThreads$;
      }

      return this.communication$.pipe(map(({ threads }) => threads));
    }),
  );

  currentUser$ = this.vimCoreCurrentUserService.authorizedUser$.pipe(filterEmpty());
  currentUserAvatarUrl$ = this.currentUser$.pipe(map(({ avatarUrl }) => avatarUrl));

  MessageType = MessageType;

  replyingThreadId$ = this.store.select('replyingThreadId');
  expandedThreadIds$ = this.store.select('expandedThreadIds');

  currentUserId = this.vimCoreCurrentUserService.getUser()?.id;

  isLoading$ = this.communicationFormService.isLoading$.pipe(startWith(true));

  constructor() {
    registerTranslationsManuallyStatic('skyproCommunications', this.translocoService, {
      en: entranslation,
      ru: rutranslation,
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.highlightedThreadId) {
      this.setHighlightedThreadId(this.highlightedThreadId);
    }

    if (changes?.communicationId) {
      this.setCommunicationId(this.communicationId);
    }
  }

  ngOnInit(): void {
    this.initCommunicationPolling();

    this.initUserExpirienceEchancer();
  }

  private initUserExpirienceEchancer(): void {
    this.communication$
      .pipe(
        tap(communication => {
          const savedExpandedThreadIds = this.store.get('expandedThreadIds');

          if (savedExpandedThreadIds.length === 0) {
            this.initExpandedThreads(communication);
          }
        }),
        takeUntilDestroyed(this),
      )
      .subscribe();
  }

  private initCommunicationPolling(): void {
    combineLatest([
      this.store.select('communicationId').pipe(
        tap(() => {
          this.isInitalCommunicationLoadingSubj.next(true);
        }),
        filterEmpty(),
      ),
      this.lastThreadIdCursor$,
    ])
      .pipe(
        switchMap(([communicationId, lastThreadId]) => {
          return this.communicationFormService.pollCommunication(communicationId, lastThreadId, this.sortByOwnThreads);
        }),
        map(result => this.filterCommunication(result)),
        tap(communication => this.emitQuestionCount(communication.threadsCount)),
        tap(() => {
          this.isInitalCommunicationLoadingSubj.next(false);
        }),
        takeUntilDestroyed(this),
      )
      .subscribe(communication => {
        this.store.updateFields({
          communication,
        });
      });
  }

  identify(_index: number, item: { id: number }): number {
    return item.id;
  }

  showCreateNewThreadForm(): void {
    this.store.updateField('isCreateNewThreadFormVisible', true);
  }

  hideCreateNewThreadForm(): void {
    this.store.updateField('isCreateNewThreadFormVisible', false);
  }

  setCommunicationId(communicationId: number | null): void {
    this.store.updateField('communicationId', communicationId);
  }

  private getLastThreadIdFromCommunication(communication: Communication): number | null {
    return communication.threads.length === 0 ? null : communication.threads[communication.threads.length - 1].id;
  }

  private initExpandedThreads(communication: Communication): void {
    const expandedThreadIds = communication.threads.reduce((threadIds, thread): number[] => {
      if (thread.messages.length > 0 && thread.messages.length < 3) {
        return [...threadIds, thread.id];
      }
      return threadIds;
    }, [] as number[]);
    this.store.updateField('expandedThreadIds', expandedThreadIds);
  }

  private filterDeletedThreads(communication: Communication): Communication {
    return {
      ...communication,
      threads: communication.threads.filter(({ isDeleted, messages }) => {
        if (!isDeleted) {
          return true;
        }

        return messages.length > 0;
      }),
    };
  }

  private filterCommunication(communication: Communication): Communication {
    let processedCommunication = this.getCommunicationWithTextMessages(communication);

    if (this.communicationModifierCallback) {
      processedCommunication = this.communicationModifierCallback(processedCommunication);
    }
    if (!this.showEmptyDeletedThreads) {
      processedCommunication = this.filterDeletedThreads(processedCommunication);
    }

    return processedCommunication;
  }

  private getCommunicationWithTextMessages(communication: Communication): Communication {
    return {
      ...communication,
      threads: communication.threads.map(thread => {
        return {
          ...thread,
          messages: thread.messages.filter(({ type }) => type === MessageType.text),
        };
      }),
    };
  }

  private emitQuestionCount(value: number): void {
    this.questionCountChange.emit(value);
  }

  setReplyingThreadId(threadId: number | null): void {
    this.store.updateField('replyingThreadId', threadId);
  }

  addExpandedThreadId(threadId: number): void {
    this.store.updateField('expandedThreadIds', [...this.store.get('expandedThreadIds'), threadId]);
    this.setReplyingThreadId(threadId);
  }

  updateExpandedThread(threadId: number): void {
    const expandedThreads = this.store.get('expandedThreadIds');

    if (!expandedThreads.includes(threadId)) {
      this.addExpandedThreadId(threadId);
    }
  }

  removeExpandedThreadId(threadId: number): void {
    this.store.updateField(
      'expandedThreadIds',
      this.store.get('expandedThreadIds').filter(id => id !== threadId),
    );
    this.setReplyingThreadId(null);
  }

  setHighlightedThreadId(threadId: number | null): void {
    this.store.updateField('highlightedThreadId', threadId);

    if (threadId !== null) {
      this.store.updateField('showOnlyHighlighted', true);
      this.addExpandedThreadId(threadId);
    }
  }

  emitCloseThread(threadId: number): void {
    this.closeThread.emit(threadId);
  }

  emitPinThread(threadId: number): void {
    this.communicationFormService.pinThread(threadId).pipe(takeUntilDestroyed(this)).subscribe();
  }

  emitUnpinThread(threadId: number): void {
    this.communicationFormService.unpinThread(threadId).pipe(takeUntilDestroyed(this)).subscribe();
  }

  emitHideThread(threadId: number): void {
    this.communicationFormService.hideThread(threadId).pipe(takeUntilDestroyed(this)).subscribe();
  }

  emitShowThread(threadId: number): void {
    this.communicationFormService.showThread(threadId).pipe(takeUntilDestroyed(this)).subscribe();
  }

  deleteThread(threadId: number): void {
    this.communicationFormService.deleteThread(threadId).pipe(takeUntilDestroyed(this)).subscribe();
  }

  deleteMessage(messageId: number): void {
    this.communicationFormService.deleteMessageInThread(messageId).pipe(takeUntilDestroyed(this)).subscribe();
  }

  startEditing(messageId: number): void {
    this.store.updateField('editingMessageId', messageId);
  }

  finishEditing(): void {
    this.store.updateField('editingMessageId', null);
  }

  like(isLiked: boolean, contentId: number): void {
    this.communicationLikeService.switchLike(isLiked, contentId).pipe(takeUntilDestroyed(this)).subscribe();
  }

  openReportDialog(content: LikeableContent): void {
    this.dsDialogService
      .open<ReportDialogInput, void>(CommunicationReportDialogComponent, content)
      .onClose()
      .pipe(takeUntilDestroyed(this))
      .subscribe(({ rejected }) => {
        if (rejected) {
          return;
        }

        // eslint-disable-next-line functional/immutable-data
        content.isDislikePressed = true;
        this.changeDetectorRef.detectChanges();
      });
  }

  showAllMessages(): void {
    this.store.updateFields({
      showOnlyHighlighted: false,
    });
  }

  checkIfHasCallTutorButton(messages: Message[]): boolean {
    const tutorAnswers = messages.filter(
      ({ type, author: { hasTutorIcon } }) => type === MessageType.text && hasTutorIcon,
    );
    return tutorAnswers.length >= 2;
  }
}
